import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import { DAZE_DISCONTINUE_RULE, DISCONTINUE_TIMER } from '../../../constants/time';
import AudioContainer from '../../../common/audio/audioContainer';
import PassageLayout from '../../../common/layouts/passageLayout';
import ClozeText from '../../../common/components/ClozeText';
import GoButton from '../../../common/components/GoButton';
import TextPaneScroll from '../../../common/textPaneScroll/TextPaneScroll';
import TimedView from '../../../common/timer/TimedView';
import ScreenErrorContainer from '../../../common/error/ScreenErrorContainer';
import withBlockedBackNavigation from '../../../common/utils/withBlockedBackNavigation';
import { ApolloContext } from '../../../ApolloContextProvider';
import ConfirmPopup from '../../../common/components/ConfirmPopup';
import { ARIA_LABELS } from '../../../constants/text';
import { getScrollStepSize } from '../../mazeUtils';
import { dataDogAddAction } from '../../../common/logging/datadog';

class MazePassageView extends TimedView {
    constructor(props) {
        super(props);
        this.getScrollableContent = this.getScrollableContent.bind(this);
        this.setUpTimer = this.setUpTimer.bind(this);
        this.onRetry = this.onRetry.bind(this);
        this.isTimerDisabled = this.isTimerDisabled.bind(this);
        this.isAccessibilityConfigReady = this.isAccessibilityConfigReady.bind(this);
        this.isDiscontinued = false;
        this.timerScheduled = false;
        this.isMazeUntimedReported = false;
        this.toggleConfirmPopup = this.toggleConfirmPopup.bind(this);
        this.onConfirm = this.onConfirm.bind(this);
        this.onOkButtonPressed = this.onOkButtonPressed.bind(this);
        this.state = {
            showPopup: false,
        };
    }

    isTimerDisabled() {
        return (
            this.context.isMazeUntimed &&
            this.context.isLouisianaLicenseAvailable &&
            this.props.flowStrategy &&
            this.props.flowStrategy.supportsUntimedAssessment
        );
    }

    isAccessibilityConfigReady() {
        return !this.context.accessibilityConfigLoading && !this.context.accessibilityConfigError;
    }

    componentDidMount() {
        if (!this.props.isDataFetched) {
            this.props.fetchContent();
        } else {
            this.props.initializePassage();
            this.setUpTimer();
            this.reportIsMazeUntimedIfReady();
        }
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.isNextButtonActive && this.props.isNextButtonActive) {
            this.scrollableView.scrollDown();
        }

        if (!prevProps.isDataFetched && this.props.isDataFetched) {
            this.props.initializePassage();
        }

        // Re-run on every render (props or context change) so the timer and
        // is_maze_untimed write fire as soon as the accessibility config query
        // resolves; both calls are idempotent via guard flags.
        if (this.props.isDataFetched) {
            this.setUpTimer();
            this.reportIsMazeUntimedIfReady();
        }
    }

    componentWillUnmount() {
        this.resetTimers();
        this.props.cleanup();
    }

    reportIsMazeUntimedIfReady() {
        if (this.isMazeUntimedReported || !this.isAccessibilityConfigReady()) {
            return;
        }
        this.isMazeUntimedReported = true;
        this.props.setIsMazeUntimed(this.context.isMazeUntimed);
    }

    setUpTimer() {
        if (this.timerScheduled || !this.isAccessibilityConfigReady()) {
            return;
        }
        this.timerScheduled = true;
        if (!this.isTimerDisabled()) {
            const discontinueDaze = () => {
                this.isDiscontinued = true;
                this.props.onNext(this.isDiscontinued);
            };
            this.scheduleEvent(DISCONTINUE_TIMER, discontinueDaze, DAZE_DISCONTINUE_RULE.TIME);
        }
    }

    onRetry() {
        this.props.onNext(this.isDiscontinued, true);
    }

    toggleConfirmPopup(val) {
        this.setState({ showPopup: val });
    }

    getScrollableContent() {
        const titleSegment = <h1 key="text-title">{this.props.title}</h1>;
        const textSegment = (
            <div className="lined-text">
                {this.props.paragraphs.map(paragraph => (
                    <ClozeText
                        isTabIndexActive={!this.props.isScreenBlocked && !this.props.isGreyedOut}
                        key={paragraph.id}
                        text={paragraph.text}
                        questions={this.props.questions}
                        selectedResponses={this.props.selectedResponses}
                        onResponseSelected={this.props.onResponseSelected}
                    />
                ))}
            </div>
        );
        const buttonSegment =
            (this.props.isDataFetched && this.isTimerDisabled()) ||
            this.props.isNextButtonActive ? (
                <GoButton
                    isEnabled={!this.props.isScreenBlocked}
                    disabled={this.props.isOkButtonGreyedOut}
                    onClick={this.onOkButtonPressed}
                    ariaLabel={ARIA_LABELS.DONE}
                />
            ) : null;
        return [titleSegment, textSegment, buttonSegment];
    }

    onOkButtonPressed() {
        // STEAM-8604: record the OK-button press so we can correlate it in prod
        // with the screen block that follows (onNext) and any save failure.
        // This fires on every normal completion (~100k/day), so it is a sampled
        // RUM action rather than a warn log — it stays out of the log stream.
        // timerDisabled=true means an untimed student → opens the confirm
        // popup; otherwise the press calls onNext() directly.
        dataDogAddAction('maze_ok_button_pressed', {
            timerDisabled: this.isTimerDisabled(),
            isNextButtonActive: this.props.isNextButtonActive,
        });
        if (this.isTimerDisabled()) {
            this.toggleConfirmPopup(true);
        } else {
            this.props.onNext();
        }
    }

    onConfirm() {
        this.props.onNext();
        this.toggleConfirmPopup(false);
    }

    render() {
        return (
            <PassageLayout
                class={classNames(`daze-measure`, {
                    'is-extended-view': this.props.isExtendedView,
                })}
                isScreenBlocked={this.props.isScreenBlocked}
                isGreyedOut={this.props.isGreyedOut}
            >
                <ScreenErrorContainer onRetry={this.onRetry} />
                <AudioContainer />
                {this.state.showPopup && (
                    <ConfirmPopup
                        title="Finish Assessment"
                        message="Are you sure you’re done?"
                        confirmText="Yes, I’m done!"
                        cancelText="No, I’m still working"
                        onConfirm={this.onConfirm}
                        onCancel={() => this.toggleConfirmPopup(false)}
                    />
                )}
                <div className="text-pane">
                    <div className="daze-text">
                        <div className="content">
                            <TextPaneScroll
                                ref={node => {
                                    this.scrollableView = node;
                                }}
                                scrollToSection="segment-part-one"
                                scrollStep={getScrollStepSize(
                                    this.props.isExtendedView,
                                    this.context.fontSizeScaleValue,
                                )}
                                isEnabled={this.props.isDataFetched && this.props.isScrollEnabled}
                                content={this.getScrollableContent()}
                                isTabIndexDisabled={
                                    this.props.isGreyedOut || this.props.isScreenBlocked
                                }
                            />
                        </div>
                    </div>
                </div>
            </PassageLayout>
        );
    }
}

MazePassageView.propTypes = {
    isDataFetched: PropTypes.bool,
    isScreenBlocked: PropTypes.bool,
    isGreyedOut: PropTypes.bool,
    isOkButtonGreyedOut: PropTypes.bool,
    isNextButtonActive: PropTypes.bool,
    isScrollEnabled: PropTypes.bool,
    title: PropTypes.string,
    paragraphs: PropTypes.arrayOf(
        PropTypes.shape({
            text: PropTypes.string.isRequired,
            id: PropTypes.string.isRequired,
        }),
    ),
    questions: PropTypes.array,
    selectedResponses: PropTypes.object,
    onResponseSelected: PropTypes.func,
    fetchContent: PropTypes.func,
    onNext: PropTypes.func,
    cleanup: PropTypes.func,
    initializePassage: PropTypes.func,
    isExtendedView: PropTypes.bool,
    setIsMazeUntimed: PropTypes.func,
};

MazePassageView.defaultProps = {
    isDataFetched: false,
    isGreyedOut: false,
    isOkButtonGreyedOut: false,
    isScreenBlocked: false,
    isNextButtonActive: false,
    isScrollEnabled: false,
    title: '',
    paragraphs: [],
    questions: [],
    selectedResponses: {},
    onResponseSelected: () => {},
    fetchContent: () => {},
    onNext: () => {},
    cleanup: () => {},
    initializePassage: () => {},
    isExtendedView: false,
    setIsMazeUntimed: () => {},
};

MazePassageView.displayName = 'MazePassageView';
MazePassageView.contextType = ApolloContext;

export { MazePassageView as MazePassageViewPure };
export default withBlockedBackNavigation(MazePassageView);
